import { db, rootRef } from './firebase';
import { surveyQuestions } from '../constants/survey';
import {
  defaultEvalYear,
  peerContribDocId,
  peerSummaryDocId,
} from './evalYear';

export type AnswerValue = 'GOOD' | 'BAD' | 'NA' | string;

export type CategoryCounts = {
  Ownership: { good: number; bad: number };
  'Co-work': { good: number; bad: number };
  Professionalism: { good: number; bad: number };
};

export type PeerReportSummary = {
  evalYear: number;
  colleagueId: string;
  byCategory: CategoryCounts;
  participantCount: number;
  strengths: string[];
  improvements: string[];
  updatedAt: number;
};

export type PeerEvalContrib = {
  evalYear: number;
  evaluatorId: string;
  colleagueId: string;
  answers: Record<string, AnswerValue>;
  strength?: string;
  improvement?: string;
  status?: 'draft' | 'submitted';
  submittedAt?: number;
  updatedAt: number;
};

export function emptyCategoryCounts(): CategoryCounts {
  return {
    Ownership: { good: 0, bad: 0 },
    'Co-work': { good: 0, bad: 0 },
    Professionalism: { good: 0, bad: 0 },
  };
}

function categoryOfQuestion(questionId: number | string): keyof CategoryCounts | null {
  const q = surveyQuestions.find((item) => item.id === Number(questionId));
  return (q?.category as keyof CategoryCounts) || null;
}

function adjustCount(
  byCategory: CategoryCounts,
  questionId: number | string,
  answer: AnswerValue | undefined,
  delta: number
) {
  if (!answer || answer === 'NA') return;
  const cat = categoryOfQuestion(questionId);
  if (!cat) return;
  if (answer === 'GOOD') byCategory[cat].good = Math.max(0, byCategory[cat].good + delta);
  if (answer === 'BAD') byCategory[cat].bad = Math.max(0, byCategory[cat].bad + delta);
}

function removeOne(list: string[], value: string): string[] {
  const idx = list.indexOf(value);
  if (idx < 0) return list;
  return [...list.slice(0, idx), ...list.slice(idx + 1)];
}

function contribRef(year: number, evaluatorId: string, colleagueId: string) {
  return rootRef().collection('peer_eval_contribs').doc(peerContribDocId(year, evaluatorId, colleagueId));
}

function summaryRef(year: number, colleagueId: string) {
  return rootRef().collection('peer_report_summaries').doc(peerSummaryDocId(year, colleagueId));
}

/**
 * Spark용: 브라우저가 기여문서 + 익명 리포트를 같이 갱신 (Cloud Functions 불필요).
 * 익명 리포트 문서에는 평가자 ID를 넣지 않음.
 */
export async function upsertPeerAnswerToReport(
  evalYear: number,
  evaluatorId: string,
  colleagueId: string,
  questionId: number | string,
  answer: AnswerValue
) {
  const cRef = contribRef(evalYear, evaluatorId, colleagueId);
  const sRef = summaryRef(evalYear, colleagueId);

  await db.runTransaction(async (tx) => {
    const [cSnap, sSnap] = await Promise.all([tx.get(cRef), tx.get(sRef)]);
    const prev = (cSnap.exists ? cSnap.data() : null) as PeerEvalContrib | null;
    if (prev?.status === 'submitted') {
      throw new Error('ALREADY_SUBMITTED');
    }
    const oldAnswers = { ...(prev?.answers || {}) };
    const newAnswers = { ...oldAnswers, [String(questionId)]: answer };

    const byCategory = emptyCategoryCounts();
    if (sSnap.exists && sSnap.data()?.byCategory) {
      (Object.keys(byCategory) as (keyof CategoryCounts)[]).forEach((k) => {
        const src = sSnap.data()?.byCategory?.[k];
        byCategory[k] = { good: src?.good || 0, bad: src?.bad || 0 };
      });
    }

    adjustCount(byCategory, questionId, oldAnswers[String(questionId)], -1);
    adjustCount(byCategory, questionId, answer, +1);

    const wasParticipant = Object.keys(oldAnswers).length > 0;
    const isParticipant = Object.keys(newAnswers).length > 0;
    let participantCount = sSnap.exists ? Number(sSnap.data()?.participantCount || 0) : 0;
    if (!wasParticipant && isParticipant) participantCount += 1;
    if (wasParticipant && !isParticipant) participantCount = Math.max(0, participantCount - 1);

    tx.set(
      cRef,
      {
        evalYear,
        evaluatorId,
        colleagueId,
        answers: newAnswers,
        strength: prev?.strength || '',
        improvement: prev?.improvement || '',
        updatedAt: Date.now(),
      },
      { merge: true }
    );

    tx.set(
      sRef,
      {
        evalYear,
        colleagueId,
        byCategory,
        participantCount,
        strengths: sSnap.exists ? sSnap.data()?.strengths || [] : [],
        improvements: sSnap.exists ? sSnap.data()?.improvements || [] : [],
        updatedAt: Date.now(),
        source: 'client_dual_write',
      },
      { merge: true }
    );
  });
}

/** 서술 코멘트: contrib + 익명 텍스트 집계 */
export async function upsertPeerCommentsToReport(
  evalYear: number,
  evaluatorId: string,
  colleagueId: string,
  strength: string,
  improvement: string
) {
  const cRef = contribRef(evalYear, evaluatorId, colleagueId);
  const sRef = summaryRef(evalYear, colleagueId);
  const nextStrength = (strength || '').trim();
  const nextImprovement = (improvement || '').trim();

  await db.runTransaction(async (tx) => {
    const [cSnap, sSnap] = await Promise.all([tx.get(cRef), tx.get(sRef)]);
    const prev = (cSnap.exists ? cSnap.data() : null) as PeerEvalContrib | null;
    if (prev?.status === 'submitted') {
      throw new Error('ALREADY_SUBMITTED');
    }
    const oldStrength = (prev?.strength || '').trim();
    const oldImprovement = (prev?.improvement || '').trim();

    let strengths: string[] = sSnap.exists ? [...(sSnap.data()?.strengths || [])] : [];
    let improvements: string[] = sSnap.exists ? [...(sSnap.data()?.improvements || [])] : [];

    if (oldStrength) strengths = removeOne(strengths, oldStrength);
    if (nextStrength) strengths = [...strengths, nextStrength];
    if (oldImprovement) improvements = removeOne(improvements, oldImprovement);
    if (nextImprovement) improvements = [...improvements, nextImprovement];

    tx.set(
      cRef,
      {
        evalYear,
        evaluatorId,
        colleagueId,
        answers: prev?.answers || {},
        strength: nextStrength,
        improvement: nextImprovement,
        updatedAt: Date.now(),
      },
      { merge: true }
    );

    const byCategory = emptyCategoryCounts();
    if (sSnap.exists && sSnap.data()?.byCategory) {
      (Object.keys(byCategory) as (keyof CategoryCounts)[]).forEach((k) => {
        const src = sSnap.data()?.byCategory?.[k];
        byCategory[k] = { good: src?.good || 0, bad: src?.bad || 0 };
      });
    }

    tx.set(
      sRef,
      {
        evalYear,
        colleagueId,
        byCategory,
        participantCount: sSnap.exists ? Number(sSnap.data()?.participantCount || 0) : 0,
        strengths,
        improvements,
        updatedAt: Date.now(),
        source: 'client_dual_write',
      },
      { merge: true }
    );
  });
}

/** 남은 contrib로 익명 리포트 재작성 (멤버 삭제 등 HR 정리용) */
export async function rebuildPeerSummaryFromContribs(
  evalYear: number,
  colleagueId: string
): Promise<{ participantCount: number; deleted: boolean }> {
  const snap = await rootRef()
    .collection('peer_eval_contribs')
    .where('colleagueId', '==', colleagueId)
    .where('evalYear', '==', evalYear)
    .get();

  const sRef = summaryRef(evalYear, colleagueId);
  if (snap.empty) {
    await sRef.delete().catch(() => undefined);
    return { participantCount: 0, deleted: true };
  }

  const byCategory = emptyCategoryCounts();
  let participantCount = 0;
  const strengths: string[] = [];
  const improvements: string[] = [];

  snap.docs.forEach((doc) => {
    const d = doc.data();
    const answers = (d.answers || {}) as Record<string, AnswerValue>;
    if (Object.keys(answers).length > 0) participantCount += 1;
    Object.entries(answers).forEach(([qid, ans]) => {
      adjustCount(byCategory, qid, ans, +1);
    });
    const s = String(d.strength || '').trim();
    const i = String(d.improvement || '').trim();
    if (s) strengths.push(s);
    if (i) improvements.push(i);
  });

  await sRef.set({
    evalYear,
    colleagueId,
    byCategory,
    participantCount,
    strengths,
    improvements,
    updatedAt: Date.now(),
    source: 'client_rebuild',
  });

  return { participantCount, deleted: false };
}

type RawAnswer = {
  evalYear?: number;
  evaluatorId?: string;
  colleagueId?: string;
  questionId?: number | string;
  answer?: AnswerValue;
};

type RawComment = {
  evalYear?: number;
  evaluatorId?: string;
  colleagueId?: string;
  strength?: string;
  improvement?: string;
};

/**
 * 기존 peer_evals / peer_comments → contrib + 익명 리포트 재구성 (HR, 브라우저).
 */
export async function backfillPeerReportSummaries(): Promise<{
  contribCount: number;
  summaryCount: number;
}> {
  const fallbackYear = defaultEvalYear();
  const [evalSnap, commentSnap] = await Promise.all([
    rootRef().collection('peer_evals').get(),
    rootRef().collection('peer_comments').get(),
  ]);

  type PairAgg = {
    evalYear: number;
    evaluatorId: string;
    colleagueId: string;
    answers: Record<string, AnswerValue>;
    strength: string;
    improvement: string;
  };

  const pairs = new Map<string, PairAgg>();

  evalSnap.docs.forEach((doc) => {
    const d = doc.data() as RawAnswer;
    if (!d.evaluatorId || !d.colleagueId || d.questionId == null || !d.answer) return;
    const year = Number(d.evalYear) || fallbackYear;
    const key = `${year}_${d.evaluatorId}_${d.colleagueId}`;
    const cur = pairs.get(key) || {
      evalYear: year,
      evaluatorId: d.evaluatorId,
      colleagueId: d.colleagueId,
      answers: {},
      strength: '',
      improvement: '',
    };
    cur.answers[String(d.questionId)] = d.answer;
    pairs.set(key, cur);
  });

  commentSnap.docs.forEach((doc) => {
    const d = doc.data() as RawComment;
    if (!d.evaluatorId || !d.colleagueId) return;
    const year = Number(d.evalYear) || fallbackYear;
    const key = `${year}_${d.evaluatorId}_${d.colleagueId}`;
    const cur = pairs.get(key) || {
      evalYear: year,
      evaluatorId: d.evaluatorId,
      colleagueId: d.colleagueId,
      answers: {},
      strength: '',
      improvement: '',
    };
    cur.strength = (d.strength || '').trim();
    cur.improvement = (d.improvement || '').trim();
    pairs.set(key, cur);
  });

  const byColleague = new Map<
    string,
    {
      evalYear: number;
      colleagueId: string;
      byCategory: CategoryCounts;
      participantCount: number;
      strengths: string[];
      improvements: string[];
    }
  >();

  const now = Date.now();
  const batchLimit = 400;
  let batch = db.batch();
  let ops = 0;
  let contribCount = 0;

  const flush = async () => {
    if (ops === 0) return;
    await batch.commit();
    batch = db.batch();
    ops = 0;
  };

  for (const pair of pairs.values()) {
    batch.set(contribRef(pair.evalYear, pair.evaluatorId, pair.colleagueId), {
      evalYear: pair.evalYear,
      evaluatorId: pair.evaluatorId,
      colleagueId: pair.colleagueId,
      answers: pair.answers,
      strength: pair.strength,
      improvement: pair.improvement,
      updatedAt: now,
    }, { merge: true });
    ops += 1;
    contribCount += 1;
    if (ops >= batchLimit) await flush();

    const sumKey = `${pair.evalYear}_${pair.colleagueId}`;
    let agg = byColleague.get(sumKey);
    if (!agg) {
      agg = {
        evalYear: pair.evalYear,
        colleagueId: pair.colleagueId,
        byCategory: emptyCategoryCounts(),
        participantCount: 0,
        strengths: [],
        improvements: [],
      };
      byColleague.set(sumKey, agg);
    }
    if (Object.keys(pair.answers).length > 0) agg.participantCount += 1;
    Object.entries(pair.answers).forEach(([qid, ans]) => {
      adjustCount(agg!.byCategory, qid, ans, +1);
    });
    if (pair.strength) agg.strengths.push(pair.strength);
    if (pair.improvement) agg.improvements.push(pair.improvement);
  }

  let summaryCount = 0;
  for (const agg of byColleague.values()) {
    batch.set(summaryRef(agg.evalYear, agg.colleagueId), {
      evalYear: agg.evalYear,
      colleagueId: agg.colleagueId,
      byCategory: agg.byCategory,
      participantCount: agg.participantCount,
      strengths: agg.strengths,
      improvements: agg.improvements,
      updatedAt: now,
      source: 'client_backfill',
    });
    ops += 1;
    summaryCount += 1;
    if (ops >= batchLimit) await flush();
  }

  await flush();
  return { contribCount, summaryCount };
}
